/**
 * NoteBlock Component
 *
 * Custom BlockNote React component for creating informational note/aside blocks
 * with configurable types (info, warning, tip, error) and color-coded backgrounds.
 *
 * Features:
 * - Customizable title and body text
 * - Four note types with distinct colors and icons
 * - Rich text support in body content
 * - Edit mode with form controls
 * - View mode matching SSR output
 * - Full accessibility support (WCAG AA)
 * - Responsive design for all devices
 *
 * Props (from block.props):
 * - title: Note heading text (required)
 * - body: Rich text content for note body (required)
 * - noteType: Type of note - info/warning/tip/error (default: "info")
 *
 * Usage:
 *   This component is automatically rendered when a "noteBlock" block
 *   is present in the BlockNote document.
 *
 * Related Files:
 * - app/components/blocknote/blocks/note_block_component.rb (SSR component)
 * - app/javascript/controllers/blocks_text_editor_controller.js
 */

import React, { useState, useEffect, useCallback } from "react";
import { markdownToHtml, htmlToMarkdown } from "../../utils/markdown";
import { sanitizeHtml } from "../../utils/sanitize";

/**
 * Note type configuration
 * Maps note types to their visual properties
 */
const NOTE_TYPE_CONFIG = {
  info: {
    bgColor: "bg-blue-100",
    iconColor: "text-blue-800",
    icon: "info-solid",
    label: "Information",
  },
  warning: {
    bgColor: "bg-yellow-100",
    iconColor: "text-yellow-900",
    icon: "warning-solid",
    label: "Warning",
  },
  tip: {
    bgColor: "bg-green-100",
    iconColor: "text-green-800",
    icon: "success", // Using success icon as lightbulb alternative
    label: "Tip",
  },
  error: {
    bgColor: "bg-red-100",
    iconColor: "text-red-800",
    icon: "circle-x-solid",
    label: "Error",
  },
};

/**
 * Icon Component
 * Renders SVG icons for note types
 */
function NoteIcon({ type }) {
  const config = NOTE_TYPE_CONFIG[type] || NOTE_TYPE_CONFIG.info;

  const getIconPath = () => {
    switch (config.icon) {
      case "info-solid":
        return (
          <path
            fillRule="evenodd"
            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
            clipRule="evenodd"
          />
        );
      case "warning-solid":
        return (
          <path
            fillRule="evenodd"
            d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
            clipRule="evenodd"
          />
        );
      case "success":
        return (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        );
      case "circle-x-solid":
        return (
          <g viewBox="0 0 640 640">
            <path d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM231 231C240.4 221.6 255.6 221.6 264.9 231L319.9 286L374.9 231C384.3 221.6 399.5 221.6 408.8 231C418.1 240.4 418.2 255.6 408.8 264.9L353.8 319.9L408.8 374.9C418.2 384.3 418.2 399.5 408.8 408.8C399.4 418.1 384.2 418.2 374.9 408.8L319.9 353.8L264.9 408.8C255.5 418.2 240.3 418.2 231 408.8C221.7 399.4 221.6 384.2 231 374.9L286 319.9L231 264.9C221.6 255.5 221.6 240.3 231 231z" />
          </g>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${config.bgColor} ${config.iconColor}`}
    >
      <svg
        className="w-6 h-6"
        fill="currentColor"
        viewBox={config.icon === "circle-x-solid" ? "0 0 640 640" : "0 0 24 24"}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {getIconPath()}
      </svg>
    </div>
  );
}

/**
 * Helper function to detect if a string contains HTML tags
 */
const containsHtml = (str) => {
  if (!str) return false;
  return /<\/?[a-z][\s\S]*>/i.test(str);
};

/**
 * Edit Mode Component
 * Provides form controls for editing note properties
 */
function NoteEditor({ block, onUpdate, bodyMarkdown, onBodyMarkdownChange }) {
  const title = block.props?.title || "";
  const noteType = block.props?.noteType || "info";

  // Handle markdown change - update local state
  const handleBodyChange = (e) => {
    onBodyMarkdownChange(e.target.value);
  };

  // Handle blur - convert markdown to HTML and save to block props
  const handleBodyBlur = () => {
    const html = markdownToHtml(bodyMarkdown);
    onUpdate("body", html);
  };

  return (
    <div className="note-block-editor w-full p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="space-y-4">
        {/* Note Type Selector */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Note Type
          </label>
          <select
            value={noteType}
            onChange={(e) => onUpdate("noteType", e.target.value)}
            className="form-control"
          >
            {Object.entries(NOTE_TYPE_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </select>
        </div>

        {/* Title Input */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => onUpdate("title", e.target.value)}
            placeholder="Enter note title..."
            maxLength={200}
            className="form-control"
          />
        </div>

        {/* Body Input */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Body
          </label>
          <textarea
            value={bodyMarkdown}
            onChange={handleBodyChange}
            onBlur={handleBodyBlur}
            placeholder="Enter note content..."
            rows={4}
            className="form-control"
          />
          <span className="mt-1 text-xs text-neutral-500">
            Supports markdown: **bold**, *italic*, [link](url), line breaks
          </span>
        </div>

        {/* Preview */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm font-medium text-neutral-700 mb-2">Preview:</p>
          <NoteViewer block={block} bodyHtml={markdownToHtml(bodyMarkdown)} />
        </div>
      </div>
    </div>
  );
}

/**
 * View Mode Component
 * Renders the note in its final display format
 */
function NoteViewer({ block, bodyHtml }) {
  const title = block.props?.title || "Note";
  const body = bodyHtml !== undefined ? bodyHtml : block.props?.body || "";
  const noteType = block.props?.noteType || "info";
  const config = NOTE_TYPE_CONFIG[noteType] || NOTE_TYPE_CONFIG.info;

  // Convert basic HTML in body to JSX
  const renderBody = () => {
    if (!body) return null;

    // Render HTML content with prose styling for proper markdown rendering
    return (
      <div
        className="note-body text-neutral-700 prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
      />
    );
  };

  return (
    <aside
      className={`note-block ${config.bgColor} rounded-lg p-6 my-4`}
      role="note"
      aria-label={`${config.label}: ${title}`}
    >
      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0" aria-hidden="true">
          <NoteIcon type={noteType} />
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3 className="font-semibold text-neutral-900 mb-2">{title}</h3>
          {renderBody()}
        </div>
      </div>
    </aside>
  );
}

/**
 * Main NoteBlock Component
 *
 * Manages edit/view mode switching and prop updates
 */
export default function NoteBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(true);

  // Initialize markdown state - if props contain HTML, convert back to markdown
  // This allows users to see and edit the original markdown syntax
  const [bodyMarkdown, setBodyMarkdown] = useState(() => {
    const body = block.props?.body || "";
    return containsHtml(body) ? htmlToMarkdown(body) : body;
  });

  // Detect edit vs view mode
  useEffect(() => {
    const editMode = editor && editor.isEditable;
    setIsEditMode(editMode);
  }, [editor]);

  // Sync markdown state when block props change externally (e.g., after drag/drop reorder)
  // We use block.id as the key dependency to detect when this is a different block instance
  useEffect(() => {
    const body = block.props?.body || "";
    const newMarkdown = containsHtml(body) ? htmlToMarkdown(body) : body;
    setBodyMarkdown(newMarkdown);
  }, [block.id]);

  // Update block props
  const handleUpdate = useCallback(
    (key, value) => {
      if (!isEditMode || !editor) return;

      editor.updateBlock(block, {
        props: {
          ...block.props,
          [key]: value,
        },
      });
    },
    [editor, block, isEditMode],
  );

  // Ensure default props
  const blockWithDefaults = {
    ...block,
    props: {
      title: block.props?.title || "",
      body: block.props?.body || "",
      noteType: block.props?.noteType || "info",
      ...block.props,
    },
  };

  return (
    <div className="blocknote-note-block w-full my-2">
      {isEditMode ? (
        <NoteEditor
          block={blockWithDefaults}
          onUpdate={handleUpdate}
          bodyMarkdown={bodyMarkdown}
          onBodyMarkdownChange={setBodyMarkdown}
        />
      ) : (
        <NoteViewer block={blockWithDefaults} />
      )}
    </div>
  );
}
